import { IResponse, IRocketTokens } from "@eventr/common";
import { InternalAxiosRequestConfig } from "axios";
import { spchatUrl } from "../feat/rocketchat/chatBackend";
import { ApiClient } from "./apiClientCtor";
import { authRequestInterceptor } from "./interseptors/authRequestInterceptor";
import { comboInterceptorResponse } from "./interseptors/comboInterResp";
import { guestAbortRequestInterceptor } from "./interseptors/guestAbortRequestInterceptor";
import { logInterReq } from "./interseptors/logInterReq";
import { logInterReqError } from "./interseptors/logInterReqError";
import { logInterResponseError } from "./interseptors/logInterResponseError";
import { spTraceInterReq } from "./interseptors/spTraceInterReq";

/** The gateway client's request chain, pointed at spchat: the same Keycloak bearer and trace headers. */
const spchatRequestInterceptor = async (request: InternalAxiosRequestConfig) => {
  request = guestAbortRequestInterceptor(request);
  request.baseURL = `${spchatUrl()}/api/`;
  request = spTraceInterReq(request);
  request = logInterReq(request);
  request = await authRequestInterceptor(request);
  return request;
};

export const spchatApiClient = new ApiClient({
  baseURL: "",
  timeout: 30000,
});

spchatApiClient.setInterceptors(
  comboInterceptorResponse,
  logInterResponseError,
  spchatRequestInterceptor,
  logInterReqError,
);

export const SpchatAPI = {
  /**
   * Asks spchat to make sure the org's chat is up: it wakes a proxy org's container and
   * deliberately does nothing for a native org (whose container must stay stopped).
   */
  ensureRunning: async (orgId: string): Promise<IResponse<unknown>> => {
    return spchatApiClient.get<unknown>(`chat/ensureRunning?orgId=${encodeURIComponent(orgId)}`, {
      NOT_IN_GUEST: true,
    });
  },
  /**
   * One chat session per org the user belongs to, exchanged for their Keycloak token: the same
   * `{ org, userId, authToken }` list the gateway's `/rocketchat/tokens` answers, with
   * `authToken` a sealed spchat session that every chat call (REST and DDP) then carries.
   */
  createSessions: async (): Promise<IResponse<IRocketTokens[]>> => {
    return spchatApiClient.post<object, IRocketTokens[]>("chat/session", {}, { NOT_IN_GUEST: true });
  },
  /** Whether spchat relays the org to its Rocket.Chat container (`proxy`) or serves it itself (`native`). Administrators only. */
  getOrgMode: async (orgId: string): Promise<IResponse<SpchatOrgModeState>> => {
    return spchatApiClient.get<SpchatOrgModeState>(`chat/orgMode?orgId=${encodeURIComponent(orgId)}`, {
      NOT_IN_GUEST: true,
    });
  },
  /** Switches the org between `proxy` and `native`. Administrators of the org only. */
  setOrgMode: async (orgId: string, mode: SpchatOrgMode): Promise<IResponse<SpchatOrgModeState>> => {
    return spchatApiClient.put<{ mode: SpchatOrgMode }, SpchatOrgModeState>(
      `chat/orgMode?orgId=${encodeURIComponent(orgId)}`,
      { mode },
    );
  },
};

export type SpchatOrgMode = "proxy" | "native";
export interface SpchatOrgModeState {
  orgId: string;
  mode: SpchatOrgMode;
}
